import { 
  Box, Typography, IconButton, CircularProgress,
  TextField, Button, FormControl, InputLabel, 
  Select, MenuItem, SelectChangeEvent, Tabs, Tab,
  Paper, Divider, Alert, useTheme, Tooltip
} from "@mui/material";
import { useState, useEffect } from "react";
import { Scope } from "../../types";
import { useScope } from "app/context/ScopeContext";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import UpdateIcon from "@mui/icons-material/Update";
import ModalMAIQ from "app/components/ModalMAIQ";
import ConfirmationModal from "app/components/ConfirmationModal";
import CopyToClipboard from "app/components/CopyToClipboard";
import { formatToLocalTime } from 'utils/dateUtils';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 2, px: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface ScopeDetailsModalProps {
  open: boolean;
  onClose: () => void;
  scope: Scope;
  onSuccess: () => void;
}

const ScopeDetailsModal = ({ open, onClose, scope, onSuccess }: ScopeDetailsModalProps) => {
  const theme = useTheme();
  const { editScope, deleteScope } = useScope();
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string>('');
  const [openConfirm, setOpenConfirm] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [formData, setFormData] = useState<Scope>(scope);
  const [originalData, setOriginalData] = useState<Scope>(scope);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    setFormData(scope);
    setOriginalData(scope);
    setIsEditing(false);
  }, [scope]);

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'status' ? value === 'true' : value,
    }));
  };

  const handleEditSubmit = async () => {
    setIsSubmitting(true);
    setError('');
    setOpenConfirm(false);

    try {
      if (!scope._id) {
        throw new Error('Scope ID is missing');
      }
      await editScope(scope._id, formData);
      setIsEditing(false);
      onSuccess();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteScope = async () => {
    setIsDeleting(true);
    setError('');
    setOpenDeleteConfirm(false);

    try {
      if (!scope._id) {
        throw new Error('Scope ID is missing');
      }
      await deleteScope(scope._id);
      onSuccess();
      onClose();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <ModalMAIQ 
      open={open} 
      onClose={onClose} 
      hideDefaultCloseButton={true}
    >
      <Paper
        elevation={0}
        sx={{
          width: 600,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
        }}
      >
        {/* Header - fixed */}
        <Box
          sx={{
            p: 2,
            backgroundColor: '#0F2A52',
            color: 'white',
            zIndex: 10,
            flexShrink: 0,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <Typography variant="h6" fontWeight="600">
              Scope Details
            </Typography>
            <Box>
              <Tooltip title="Edit Scope">
                <IconButton
                  onClick={() => {
                    setIsEditing(true);
                    setOriginalData(formData);
                  }}
                  sx={{ color: 'white', p: 0.5 }}
                  size="small"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Scope">
                <IconButton 
                  onClick={() => setOpenDeleteConfirm(true)} 
                  sx={{ color: 'white', p: 0.5, ml: 1 }}
                  size="small"
                >
                  {isDeleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Close">
                <IconButton
                  aria-label="close"
                  onClick={onClose}
                  sx={{
                    color: 'white',
                    p: 0.5,
                    ml: 1
                  }}
                  size="small"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Typography variant="subtitle2" sx={{ mt: 0.5, fontWeight: 400, fontSize: '0.8rem' }}>
            ID: {scope._id || scope['_id']}
            {(scope._id || scope['_id']) && <CopyToClipboard text={scope._id || scope['_id']} size="small" />}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mx: 2, mt: 2 }}>
            {error}
          </Alert>
        )}

        <Tabs 
          value={tabValue} 
          onChange={(_, newValue) => setTabValue(newValue)} 
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            "& .MuiTabs-indicator": {
              height: 4,
              backgroundColor: theme.palette.secondary.main,
            },
            flexShrink: 0,
          }}
        >
          <Tooltip title="View and edit scope properties">
            <Tab 
              label="Details" 
              sx={{ 
                textTransform: 'none', 
                fontWeight: tabValue === 0 ? 600 : 400,
                fontSize: '0.9rem',
                px: 3,
                color: tabValue === 0 ? theme.palette.secondary.main : '#666',
              }} 
            />
          </Tooltip>
        </Tabs>

        {/* Scrollable content area */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            position: 'relative',
          }}
        >
          <TabPanel value={tabValue} index={0}>
            <Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Name *
                </Typography>
                <TextField
                  fullWidth
                  name="name"
                  value={formData.name}
                  onChange={handleTextChange}
                  required
                  disabled={!isEditing || isSubmitting}
                  variant="outlined"
                  size="small"
                  inputProps={{
                    sx: { py: 1.5 }
                  }}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Scope *
                </Typography>
                <TextField
                  fullWidth
                  name="scope"
                  value={formData.scope}
                  onChange={handleTextChange}
                  required
                  disabled={!isEditing || isSubmitting}
                  variant="outlined"
                  size="small"
                  inputProps={{
                    sx: { py: 1.5 }
                  }}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Description *
                </Typography>
                <TextField
                  fullWidth
                  name="description"
                  value={formData.description}
                  onChange={handleTextChange}
                  multiline
                  rows={4}
                  required
                  disabled={!isEditing || isSubmitting}
                  variant="outlined"
                  size="small"
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Status
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    name="status"
                    value={formData.status ? 'true' : 'false'}
                    onChange={handleSelectChange}
                    required
                    disabled={!isEditing || isSubmitting}
                    displayEmpty
                    inputProps={{ 
                      sx: { py: 1.5 } 
                    }}
                  >
                    <MenuItem value="true">Active</MenuItem>
                    <MenuItem value="false">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Paper 
                variant="outlined" 
                sx={{ 
                  mt: 4, 
                  p: 2, 
                  borderRadius: 1, 
                  bgcolor: 'rgba(0, 0, 0, 0.02)',
                  border: '1px solid rgba(0, 0, 0, 0.1)'
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Metadata
                </Typography>
                <Box display="flex" flexDirection="column" gap={1}>
                  {formData.created_at && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Tooltip title="Creation Date">
                        <AccessTimeIcon fontSize="small" sx={{ color: '#666', fontSize: '1rem' }} />
                      </Tooltip>
                      <Typography variant="body2" sx={{ color: '#666', fontSize: '0.9rem' }}>
                        Created: {formatToLocalTime(formData.created_at)}
                      </Typography>
                    </Box>
                  )}

                  {formData.updated_at && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Tooltip title="Last Update Date">
                        <UpdateIcon fontSize="small" sx={{ color: '#666', fontSize: '1rem' }} />
                      </Tooltip>
                      <Typography variant="body2" sx={{ color: '#666', fontSize: '0.9rem' }}>
                        Last Updated: {formatToLocalTime(formData.updated_at)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Box>
          </TabPanel>
        </Box>

        <Divider />
        
        {/* Footer with action buttons - fixed at bottom */}
        <Box 
          sx={{ 
            p: 2, 
            display: 'flex', 
            justifyContent: 'flex-end',
            backgroundColor: 'white',
            borderTop: '1px solid rgba(0, 0, 0, 0.12)',
            zIndex: 9,
            flexShrink: 0,
          }}
        >
          {isEditing && (
            <Tooltip title="Save your changes">
              <Button
                variant="contained"
                onClick={() => setOpenConfirm(true)}
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                sx={{ 
                  textTransform: 'none', 
                  fontWeight: 'bold', 
                  bgcolor: '#0F2A52',
                  color: 'white',
                  py: 0.75,
                  px: 3
                }}
              >
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </Tooltip>
          )}
        </Box>

        <ConfirmationModal
          open={openConfirm}
          onClose={() => setOpenConfirm(false)}
          onConfirm={handleEditSubmit}
          message="Are you sure you want to save the changes to this scope?"
        />
        <ConfirmationModal
          open={openDeleteConfirm}
          onClose={() => setOpenDeleteConfirm(false)}
          onConfirm={handleDeleteScope}
          message="Are you sure you want to delete this scope?"
        />
      </Paper>
    </ModalMAIQ>
  );
};

export default ScopeDetailsModal; 